import { createFileRoute, useNavigate, useSearch } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { z } from "zod";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/auth/AuthProvider";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { toast } from "sonner";
import { ArrowLeft, Plus, Trash2, Lock } from "lucide-react";
import { ScopePhotosUploader, type ScopePhoto } from "@/components/ScopePhotosUploader";

const searchSchema = z.object({
  contractId: z.string().optional(),
  estimateId: z.string().optional(),
});

export const Route = createFileRoute("/_app/invoices/new")({
  component: NewInvoicePage,
  validateSearch: searchSchema,
});

type LineItem = {
  description: string;
  quantity: number;
  unit_price: number;
  estimate_item_id?: string | null;
  change_order_item_id?: string | null;
};
type EstimateItemBalance = {
  id: string;
  description: string;
  quantity: number;
  unit_price: number;
  total: number;
  invoiced: number;
  remaining: number;
};
type ClientOpt = {
  id: string;
  name: string;
  email: string | null;
  address_line1: string | null;
  address_line2: string | null;
  city: string | null;
  state: string | null;
  postal_code: string | null;
  country: string | null;
};
type ContractOpt = {
  id: string;
  number: string;
  title: string;
  client_id: string;
  amount: number;
  retainage_percent: number;
  currency: string | null;
  scope: string | null;
  terms: string | null;
  notes: string | null;
  end_date: string | null;
  estimate_id: string | null;
};
type EstimateOpt = {
  id: string;
  number: string;
  title: string;
  client_id: string;
  description: string | null;
};
type ChangeOrderOpt = {
  id: string;
  number: string;
  title: string;
  client_id: string | null;
  contract_id: string | null;
  estimate_id: string | null;
};
type EstimateItem = { description: string; quantity: number; unit_price: number };

function NewInvoicePage() {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { profile, company } = useAuth();
  const { contractId: prefContract, estimateId: prefEstimate } = useSearch({
    from: "/_app/invoices/new",
  });

  const [clients, setClients] = useState<ClientOpt[]>([]);
  const [contracts, setContracts] = useState<ContractOpt[]>([]);
  const [estimates, setEstimates] = useState<EstimateOpt[]>([]);
  const [changeOrders, setChangeOrders] = useState<ChangeOrderOpt[]>([]);
  const [clientId, setClientId] = useState<string>("");
  const [contractId, setContractId] = useState<string>("none");
  const [estimateId, setEstimateId] = useState<string>("none");
  const [changeOrderId, setChangeOrderId] = useState<string>("none");
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [items, setItems] = useState<LineItem[]>([{ description: "", quantity: 1, unit_price: 0 }]);
  const [taxPercent, setTaxPercent] = useState<number>(0);
  const [retainagePercent, setRetainagePercent] = useState<number>(0);
  const [dueDays, setDueDays] = useState<number>(5);
  const [saving, setSaving] = useState(false);
  const [confirmSend, setConfirmSend] = useState(false);
  const [contractItems, setContractItems] = useState<EstimateItemBalance[]>([]);
  const [loadingBalances, setLoadingBalances] = useState(false);
  const [scopePhotos, setScopePhotos] = useState<ScopePhoto[]>([]);

  useEffect(() => {
    (async () => {
      const [cl, co, es, ch] = await Promise.all([
        supabase
          .from("clients")
          .select(
            "id, name, email, address_line1, address_line2, city, state, postal_code, country",
          )
          .eq("is_active", true)
          .order("name"),
        supabase
          .from("contracts")
          .select(
            "id, number, title, client_id, amount, retainage_percent, currency, scope, terms, notes, end_date, estimate_id",
          )
          .in("status", ["signed", "active"])
          .order("created_at", { ascending: false }),
        supabase
          .from("estimates")
          .select("id, number, title, client_id, description")
          .eq("status", "approved")
          .order("created_at", { ascending: false }),
        supabase
          .from("change_orders")
          .select("id, number, title, contract_id, estimate_id, contracts:contract_id(client_id)")
          .eq("status", "approved")
          .order("created_at", { ascending: false }),
      ]);
      setClients((cl.data ?? []) as ClientOpt[]);
      setContracts((co.data ?? []) as ContractOpt[]);
      setEstimates((es.data ?? []) as EstimateOpt[]);
      setChangeOrders(
        (
          (ch.data ?? []) as Array<{
            id: string;
            number: string;
            title: string;
            contract_id: string | null;
            estimate_id: string | null;
            contracts: { client_id: string } | { client_id: string }[] | null;
          }>
        ).map((c) => {
          const contract = Array.isArray(c.contracts) ? c.contracts[0] : c.contracts;
          return {
            id: c.id,
            number: c.number,
            title: c.title,
            client_id: contract?.client_id ?? null,
            contract_id: c.contract_id,
            estimate_id: c.estimate_id,
          };
        }),
      );
    })();
  }, []);

  useEffect(() => {
    if (company?.tax_percent != null) setTaxPercent(Number(company.tax_percent));
  }, [company?.tax_percent]);

  useEffect(() => {
    if (prefContract && contracts.length) {
      const c = contracts.find((x) => x.id === prefContract);
      if (c) void applyContract(c);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [prefContract, contracts]);

  useEffect(() => {
    if (prefEstimate && estimates.length) {
      void applyEstimateById(prefEstimate);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [prefEstimate, estimates]);

  // Auto-seleção: ao escolher cliente, se há só um contrato vinculado, aplica
  useEffect(() => {
    if (!clientId || contractId !== "none" || contracts.length === 0) return;
    const clientContracts = contracts.filter((c) => c.client_id === clientId);
    if (clientContracts.length === 1) {
      void applyContract(clientContracts[0]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [clientId, contracts]);

  // Auto-seleção: ao escolher cliente sem contrato, se há só um orçamento, aplica
  useEffect(() => {
    if (!clientId || estimateId !== "none" || contractId !== "none" || estimates.length === 0)
      return;
    const clientEstimates = estimates.filter((e) => e.client_id === clientId);
    if (clientEstimates.length === 1) {
      void applyEstimateById(clientEstimates[0].id);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [clientId, estimates, contractId]);

  const applyContract = async (c: ContractOpt) => {
    setChangeOrderId("none");
    setContractId(c.id);
    setClientId(c.client_id);
    setTitle(c.title);
    if (c.retainage_percent != null) setRetainagePercent(Number(c.retainage_percent));
    const desc = c.scope || c.notes || "";
    if (desc) setDescription(desc);
    if (c.end_date) {
      const end = new Date(c.end_date);
      const diff = Math.ceil((end.getTime() - Date.now()) / (1000 * 60 * 60 * 24));
      if (diff > 0) setDueDays(diff);
    }

    if (!c.estimate_id) {
      // Sem orçamento vinculado: cai no comportamento antigo (linha única do valor)
      setContractItems([]);
      setEstimateId("none");
      setItems([
        { description: c.scope || c.title, quantity: 1, unit_price: Number(c.amount) || 0 },
      ]);
      return;
    }

    setEstimateId(c.estimate_id);
    setLoadingBalances(true);
    try {
      const { data: estItems } = await supabase
        .from("estimate_items")
        .select("id, description, quantity, unit_price, total")
        .eq("estimate_id", c.estimate_id)
        .order("sort_order");

      const itemsList = (estItems ?? []) as Array<{
        id: string;
        description: string;
        quantity: number;
        unit_price: number;
        total: number;
      }>;

      // Total já faturado por estimate_item_id.
      // Faturas canceladas não consomem saldo; faturas parciais consomem apenas
      // proporcional ao que foi efetivamente pago — o saldo devedor retorna ao contrato.
      const ids = itemsList.map((i) => i.id);
      const invoicedMap = new Map<string, number>();
      if (ids.length) {
        const { data: usedRows } = await supabase
          .from("invoice_items")
          .select("estimate_item_id, total, invoices!inner(status, total, amount_paid)")
          .in("estimate_item_id", ids);
        for (const r of (usedRows ?? []) as Array<{
          estimate_item_id: string | null;
          total: number;
          invoices: { status: string; total: number; amount_paid: number } | null;
        }>) {
          if (!r.estimate_item_id || !r.invoices) continue;
          if (r.invoices.status === "cancelled") continue;
          const itemTotal = Number(r.total || 0);
          let consumed = itemTotal;
          if (r.invoices.status === "partial") {
            const invTotal = Number(r.invoices.total || 0);
            const invPaid = Number(r.invoices.amount_paid || 0);
            const ratio = invTotal > 0 ? Math.min(1, invPaid / invTotal) : 0;
            consumed = itemTotal * ratio;
          }
          invoicedMap.set(
            r.estimate_item_id,
            (invoicedMap.get(r.estimate_item_id) ?? 0) + consumed,
          );
        }
      }

      const balances: EstimateItemBalance[] = itemsList.map((it) => {
        const total = Number(it.total) || Number(it.quantity) * Number(it.unit_price);
        const invoiced = invoicedMap.get(it.id) ?? 0;
        return {
          id: it.id,
          description: it.description,
          quantity: Number(it.quantity),
          unit_price: Number(it.unit_price),
          total,
          invoiced,
          remaining: Math.max(0, total - invoiced),
        };
      });
      setContractItems(balances);

      // Pré-preenche linhas só com itens que ainda têm saldo
      const remainingOnly = balances.filter((b) => b.remaining > 0.0001);
      if (remainingOnly.length) {
        setItems(
          remainingOnly.map((b) => ({
            description: b.description,
            quantity: b.unit_price > 0 ? +(b.remaining / b.unit_price).toFixed(4) : 1,
            unit_price: b.unit_price,
            estimate_item_id: b.id,
          })),
        );
      } else {
        toast.info(t("invoicesNew.toast.contractAllInvoiced"));
        setItems([{ description: c.title, quantity: 1, unit_price: 0 }]);
      }
    } finally {
      setLoadingBalances(false);
    }
  };

  const applyEstimateById = async (id: string) => {
    const est = estimates.find((e) => e.id === id);
    if (!est) return;
    setChangeOrderId("none");
    setEstimateId(id);
    setClientId(est.client_id);
    setTitle(est.title);
    if (est.description) setDescription(est.description);

    const { data: estItems } = await supabase
      .from("estimate_items")
      .select("id, description, quantity, unit_price, total")
      .eq("estimate_id", id)
      .order("sort_order");

    const itemsList = (estItems ?? []) as Array<{
      id: string;
      description: string;
      quantity: number;
      unit_price: number;
      total: number;
    }>;
    if (!itemsList.length) {
      setContractItems([]);
      setItems([{ description: est.title, quantity: 1, unit_price: 0 }]);
      toast.warning(t("invoicesNew.toast.estimateNoItems"), {
        description: t("invoicesNew.toast.estimateNoItemsHint"),
      });
      return;
    }

    // Calcula saldo já faturado por item.
    // Canceladas não consomem; parciais consomem só a proporção paga.
    const ids = itemsList.map((i) => i.id);
    const invoicedMap = new Map<string, number>();
    const { data: usedRows } = await supabase
      .from("invoice_items")
      .select("estimate_item_id, total, invoices!inner(status, total, amount_paid)")
      .in("estimate_item_id", ids);
    for (const r of (usedRows ?? []) as Array<{
      estimate_item_id: string | null;
      total: number;
      invoices: { status: string; total: number; amount_paid: number } | null;
    }>) {
      if (!r.estimate_item_id || !r.invoices) continue;
      if (r.invoices.status === "cancelled") continue;
      const itemTotal = Number(r.total || 0);
      let consumed = itemTotal;
      if (r.invoices.status === "partial") {
        const invTotal = Number(r.invoices.total || 0);
        const invPaid = Number(r.invoices.amount_paid || 0);
        const ratio = invTotal > 0 ? Math.min(1, invPaid / invTotal) : 0;
        consumed = itemTotal * ratio;
      }
      invoicedMap.set(r.estimate_item_id, (invoicedMap.get(r.estimate_item_id) ?? 0) + consumed);
    }

    const balances: EstimateItemBalance[] = itemsList.map((it) => {
      const total = Number(it.total) || Number(it.quantity) * Number(it.unit_price);
      const invoiced = invoicedMap.get(it.id) ?? 0;
      return {
        id: it.id,
        description: it.description,
        quantity: Number(it.quantity),
        unit_price: Number(it.unit_price),
        total,
        invoiced,
        remaining: Math.max(0, total - invoiced),
      };
    });
    setContractItems(balances);

    const remainingOnly = balances.filter((b) => b.remaining > 0.0001);
    if (remainingOnly.length) {
      setItems(
        remainingOnly.map((b) => ({
          description: b.description,
          quantity: b.unit_price > 0 ? +(b.remaining / b.unit_price).toFixed(4) : 1,
          unit_price: b.unit_price,
          estimate_item_id: b.id,
        })),
      );
    } else {
      toast.info(t("invoicesNew.toast.estimateAllInvoiced"));
    }
  };

  const applyChangeOrder = async (id: string) => {
    const co = changeOrders.find((c) => c.id === id);
    if (!co) return;
    if (!co.contract_id) {
      toast.error(t("pages.invoices.new.changeOrderNoContract"));
      return;
    }
    setChangeOrderId(id);
    // A change order já nasce ligada a um contrato assinado/ativo e ao orçamento dele:
    // faturamos vinculando a fatura a esse contrato/orçamento, mas com saldo por item da CO.
    setContractId(co.contract_id);
    setEstimateId(co.estimate_id ?? "none");
    if (co.client_id) setClientId(co.client_id);
    setTitle(co.title);
    const contract = contracts.find((c) => c.id === co.contract_id);
    if (contract?.retainage_percent != null) {
      setRetainagePercent(Number(contract.retainage_percent));
    }

    const { data: coItems } = await supabase
      .from("change_order_items")
      .select("id, description, quantity, unit_price, total")
      .eq("change_order_id", id)
      .order("sort_order");

    const itemsList = (coItems ?? []) as Array<{
      id: string;
      description: string;
      quantity: number;
      unit_price: number;
      total: number;
    }>;
    if (!itemsList.length) {
      setContractItems([]);
      setItems([{ description: co.title, quantity: 1, unit_price: 0 }]);
      return;
    }

    // Saldo já faturado por item da CO. Canceladas não consomem;
    // parciais consomem só a proporção paga (igual ao fluxo de contrato).
    const ids = itemsList.map((i) => i.id);
    const invoicedMap = new Map<string, number>();
    const { data: usedRows } = await supabase
      .from("invoice_items")
      .select("change_order_item_id, total, invoices!inner(status, total, amount_paid)")
      .in("change_order_item_id", ids);
    for (const r of (usedRows ?? []) as Array<{
      change_order_item_id: string | null;
      total: number;
      invoices: { status: string; total: number; amount_paid: number } | null;
    }>) {
      if (!r.change_order_item_id || !r.invoices) continue;
      if (r.invoices.status === "cancelled") continue;
      const itemTotal = Number(r.total || 0);
      let consumed = itemTotal;
      if (r.invoices.status === "partial") {
        const invTotal = Number(r.invoices.total || 0);
        const invPaid = Number(r.invoices.amount_paid || 0);
        const ratio = invTotal > 0 ? Math.min(1, invPaid / invTotal) : 0;
        consumed = itemTotal * ratio;
      }
      invoicedMap.set(
        r.change_order_item_id,
        (invoicedMap.get(r.change_order_item_id) ?? 0) + consumed,
      );
    }

    const balances: EstimateItemBalance[] = itemsList.map((it) => {
      const total = Number(it.total) || Number(it.quantity) * Number(it.unit_price);
      const invoiced = invoicedMap.get(it.id) ?? 0;
      return {
        id: it.id,
        description: it.description,
        quantity: Number(it.quantity),
        unit_price: Number(it.unit_price),
        total,
        invoiced,
        remaining: Math.max(0, total - invoiced),
      };
    });
    setContractItems(balances);

    const remainingOnly = balances.filter((b) => b.remaining > 0.0001);
    if (remainingOnly.length) {
      setItems(
        remainingOnly.map((b) => ({
          description: b.description,
          quantity: b.unit_price > 0 ? +(b.remaining / b.unit_price).toFixed(4) : 1,
          unit_price: b.unit_price,
          change_order_item_id: b.id,
        })),
      );
    } else {
      toast.info(t("pages.invoices.new.changeOrderAllInvoiced"));
    }
  };

  const balanceIsChangeOrder = changeOrderId !== "none";
  const balanceTitle = balanceIsChangeOrder
    ? t("pages.invoices.new.balanceSectionTitleChangeOrder")
    : t("pages.invoices.new.balanceSectionTitleContract");
  const balanceHint = balanceIsChangeOrder
    ? t("pages.invoices.new.balanceSectionHintChangeOrder")
    : t("pages.invoices.new.balanceSectionHintContract");
  const balanceColSource = balanceIsChangeOrder
    ? t("pages.invoices.new.balanceColSourceChangeOrder")
    : t("pages.invoices.new.balanceColSourceContract");
  const lockedItemsText = balanceIsChangeOrder
    ? t("pages.invoices.new.lockedItemsChangeOrder")
    : t("pages.invoices.new.lockedItemsContract");

  const subtotal = items.reduce(
    (s, i) => s + (Number(i.quantity) || 0) * (Number(i.unit_price) || 0),
    0,
  );
  const taxAmount = (subtotal * (taxPercent || 0)) / 100;
  const retainageAmount = ((subtotal + taxAmount) * (retainagePercent || 0)) / 100;
  const total = subtotal + taxAmount - retainageAmount;
  const currency = company?.currency || "USD";

  const fm = (v: number) =>
    new Intl.NumberFormat("en-US", { style: "currency", currency }).format(v || 0);

  const updateItem = (idx: number, patch: Partial<LineItem>) =>
    setItems((p) => p.map((it, i) => (i === idx ? { ...it, ...patch } : it)));
  const addItem = () => setItems((p) => [...p, { description: "", quantity: 1, unit_price: 0 }]);
  const removeItem = (idx: number) =>
    setItems((p) => (p.length === 1 ? p : p.filter((_, i) => i !== idx)));

  const save = async (status: "draft" | "sent") => {
    if (!clientId) return toast.error(t("pages.invoices.new.errClient"));
    if (contractId === "none") {
      return toast.error(
        "É necessário vincular um contrato assinado ou ativo para criar a fatura.",
      );
    }
    if (!title.trim()) return toast.error(t("pages.invoices.new.errTitle"));
    const cleanItems = items.filter((i) => i.description.trim());
    if (cleanItems.length === 0) return toast.error(t("pages.invoices.new.errItem"));
    if (!company?.id || !profile?.id) return toast.error(t("pages.invoices.new.errContext"));

    // Validação de saldo por item (do contrato ou da change order)
    if (contractItems.length > 0) {
      const sourceNoun = balanceIsChangeOrder ? "da ordem de alteração" : "do contrato";
      const sumByItem = new Map<string, number>();
      for (const it of cleanItems) {
        const linkId = balanceIsChangeOrder ? it.change_order_item_id : it.estimate_item_id;
        if (!linkId) {
          return toast.error(
            `Toda linha deve estar vinculada a um item ${sourceNoun}. Remova linhas avulsas.`,
          );
        }
        const v = (Number(it.quantity) || 0) * (Number(it.unit_price) || 0);
        sumByItem.set(linkId, (sumByItem.get(linkId) ?? 0) + v);
      }
      for (const [eid, used] of sumByItem) {
        const bal = contractItems.find((b) => b.id === eid);
        if (!bal) continue;
        if (used > bal.remaining + 0.01) {
          return toast.error(
            `Item "${bal.description}" excede o saldo ${sourceNoun}. Disponível: ${fm(bal.remaining)}, tentando faturar: ${fm(used)}.`,
          );
        }
      }
    }

    setSaving(true);
    try {
      const due = new Date();
      due.setDate(due.getDate() + (dueDays || 0));
      const dueIso = due.toISOString().slice(0, 10);

      const client = clients.find((c) => c.id === clientId);
      const contract = contractId !== "none" ? contracts.find((c) => c.id === contractId) : null;
      const addressParts = [
        client?.address_line1,
        client?.address_line2,
        [client?.city, client?.state, client?.postal_code].filter(Boolean).join(", "),
        client?.country,
      ].filter(Boolean);

      const finalPhotos = scopePhotos
        .filter((p) => p.path && !p.uploading)
        .map((p) => ({
          path: p.path,
          ...(p.caption?.trim() ? { caption: p.caption.trim() } : {}),
        }));
      const { data: inv, error: iErr } = await supabase
        .from("invoices")
        .insert({
          company_id: company.id,
          client_id: clientId,
          contract_id: contractId !== "none" ? contractId : null,
          estimate_id: estimateId !== "none" ? estimateId : null,
          created_by: profile.id,
          title: title.trim(),
          description: description.trim() || null,
          status,
          tax_percent: taxPercent,
          retainage_percent: retainagePercent,
          currency,
          due_date: dueIso,
          number: "",
          client_snapshot_name: client?.name ?? null,
          client_snapshot_email: client?.email ?? null,
          client_snapshot_address: addressParts.length ? addressParts.join("\n") : null,
          contract_snapshot_number: contract?.number ?? null,
          contract_snapshot_title: contract?.title ?? null,
          scope_photos: finalPhotos,
        })
        .select("id")
        .single();
      if (iErr || !inv) throw iErr ?? new Error("Failed to create invoice");

      const { error: itErr } = await supabase.from("invoice_items").insert(
        cleanItems.map((it, idx) => ({
          invoice_id: inv.id,
          description: it.description.trim(),
          quantity: Number(it.quantity) || 0,
          unit_price: Number(it.unit_price) || 0,
          sort_order: idx,
          estimate_item_id: it.estimate_item_id ?? null,
          change_order_item_id: it.change_order_item_id ?? null,
        })),
      );
      if (itErr) throw itErr;

      toast.success(
        status === "draft"
          ? t("pages.invoices.new.draftSaved")
          : t("pages.invoices.new.invoiceSaved"),
      );
      navigate({ to: "/invoices/$invoiceId", params: { invoiceId: inv.id } });
    } catch (e) {
      const msg = e instanceof Error ? e.message : t("pages.invoices.new.couldNotSave");
      toast.error(t("pages.invoices.new.saveFailed"), { description: msg });
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-3">
        <Button variant="ghost" size="icon" onClick={() => navigate({ to: "/invoices" })}>
          <ArrowLeft className="h-4 w-4" />
        </Button>
        <div>
          <h1 className="text-2xl font-semibold tracking-tight">
            {t("pages.invoices.new.pageTitle")}
          </h1>
          <p className="text-sm text-muted-foreground">{t("pages.invoices.new.pageSubtitle")}</p>
        </div>
      </div>

      <div className="grid gap-4 rounded-lg border border-border bg-card p-5 sm:grid-cols-2">
        <div className="space-y-1.5">
          <Label>{t("pages.invoices.new.clientLabel")}</Label>
          <Select value={clientId} onValueChange={setClientId}>
            <SelectTrigger>
              <SelectValue placeholder={t("pages.invoices.new.selectClient")} />
            </SelectTrigger>
            <SelectContent>
              {clients.map((c) => (
                <SelectItem key={c.id} value={c.id}>
                  {c.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div className="space-y-1.5">
          <Label>{t("pages.invoices.new.fromContract")}</Label>
          <Select
            value={contractId}
            onValueChange={(v) => {
              if (v === "none") {
                setContractId("none");
                setContractItems([]);
                return;
              }
              const c = contracts.find((x) => x.id === v);
              if (c) void applyContract(c);
            }}
          >
            <SelectTrigger>
              <SelectValue placeholder={t("pages.invoices.new.placeholderNone")} />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="none">{t("pages.invoices.new.noneOption")}</SelectItem>
              {contracts
                .filter((c) => !clientId || c.client_id === clientId)
                .map((c) => (
                  <SelectItem key={c.id} value={c.id}>
                    {c.number} — {c.title}
                  </SelectItem>
                ))}
            </SelectContent>
          </Select>
        </div>
        <div className="space-y-1.5">
          <Label>{t("pages.invoices.new.fromEstimate")}</Label>
          <Select
            value={estimateId}
            onValueChange={(v) => {
              if (v === "none") return setEstimateId("none");
              void applyEstimateById(v);
            }}
          >
            <SelectTrigger>
              <SelectValue placeholder={t("pages.invoices.new.placeholderNone")} />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="none">{t("pages.invoices.new.noneOption")}</SelectItem>
              {(() => {
                const selectedContract =
                  contractId !== "none" ? contracts.find((c) => c.id === contractId) : null;
                return estimates
                  .filter((e) => {
                    if (selectedContract?.estimate_id) {
                      return e.id === selectedContract.estimate_id;
                    }
                    if (clientId) return e.client_id === clientId;
                    return true;
                  })
                  .map((e) => (
                    <SelectItem key={e.id} value={e.id}>
                      {e.number} — {e.title}
                    </SelectItem>
                  ));
              })()}
            </SelectContent>
          </Select>
        </div>
        <div className="space-y-1.5">
          <Label>{t("pages.invoices.new.fromChangeOrder")}</Label>
          <Select
            value={changeOrderId}
            onValueChange={(v) => {
              if (v === "none") {
                setChangeOrderId("none");
                setContractItems([]);
                setItems([{ description: "", quantity: 1, unit_price: 0 }]);
                return;
              }
              void applyChangeOrder(v);
            }}
          >
            <SelectTrigger>
              <SelectValue placeholder={t("pages.invoices.new.placeholderNone")} />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="none">{t("pages.invoices.new.noneOption")}</SelectItem>
              {changeOrders
                .filter((c) => {
                  if (contractId !== "none") return c.contract_id === contractId;
                  if (clientId) return c.client_id === clientId;
                  return true;
                })
                .map((c) => (
                  <SelectItem key={c.id} value={c.id}>
                    {c.number} — {c.title}
                  </SelectItem>
                ))}
            </SelectContent>
          </Select>
        </div>
        <div className="space-y-1.5">
          <Label>{t("pages.invoices.new.titleLabel")}</Label>
          <Input
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            placeholder={t("pages.invoices.new.titlePlaceholder")}
          />
        </div>
        <div className="space-y-1.5 sm:col-span-2">
          <Label>{t("pages.invoices.new.descriptionLabel")}</Label>
          <Textarea
            rows={2}
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            placeholder={t("pages.invoices.new.descriptionOptional")}
          />
        </div>
        <div className="space-y-1.5 sm:col-span-2">
          <Label>{t("pages.estimates.form.scopePhotos")}</Label>
          <ScopePhotosUploader
            bucket="invoice-scope-photos"
            companyId={company?.id}
            photos={scopePhotos}
            setPhotos={setScopePhotos}
          />
        </div>
        <div className="space-y-1.5">
          <Label>{t("pages.invoices.new.taxPercentLabel")}</Label>
          <Input
            type="number"
            step="0.01"
            min="0"
            value={taxPercent}
            onChange={(e) => setTaxPercent(Number(e.target.value) || 0)}
          />
        </div>
        <div className="space-y-1.5">
          <Label>{t("pages.invoices.new.retainagePercentLabel")}</Label>
          <Input
            type="number"
            step="0.01"
            min="0"
            max="100"
            value={retainagePercent}
            onChange={(e) => setRetainagePercent(Number(e.target.value) || 0)}
          />
        </div>
        <div className="space-y-1.5">
          <Label>{t("pages.invoices.new.dueDays")}</Label>
          <Input
            type="number"
            min="0"
            value={dueDays}
            onChange={(e) => setDueDays(Number(e.target.value) || 0)}
          />
        </div>
      </div>

      {contractItems.length > 0 && (
        <div className="rounded-lg border border-border bg-card">
          <div className="border-b border-border p-4">
            <p className="text-sm font-semibold">{balanceTitle}</p>
            <p className="text-xs text-muted-foreground">{balanceHint}</p>
          </div>
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>{balanceColSource}</TableHead>
                  <TableHead className="w-32 text-right">
                    {t("pages.invoices.new.balanceColDocTotal")}
                  </TableHead>
                  <TableHead className="w-32 text-right">
                    {t("pages.invoices.new.balanceColInvoiced")}
                  </TableHead>
                  <TableHead className="w-32 text-right">
                    {t("pages.invoices.new.balanceColRemaining")}
                  </TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {contractItems.map((b) => (
                  <TableRow key={b.id}>
                    <TableCell className="text-sm">{b.description}</TableCell>
                    <TableCell className="text-right text-sm">{fm(b.total)}</TableCell>
                    <TableCell className="text-right text-sm text-muted-foreground">
                      {fm(b.invoiced)}
                    </TableCell>
                    <TableCell
                      className={`text-right text-sm font-semibold ${b.remaining <= 0.01 ? "text-destructive" : ""}`}
                    >
                      {fm(b.remaining)}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        </div>
      )}

      <div className="rounded-lg border border-border bg-card">
        <div className="flex items-center justify-between border-b border-border p-4">
          <p className="text-sm font-semibold">{t("pages.invoices.new.lineItemsTitle")}</p>
          {contractItems.length === 0 ? (
            <Button variant="outline" size="sm" onClick={addItem} className="gap-2">
              <Plus className="h-3.5 w-3.5" />
              {t("pages.invoices.new.addItem")}
            </Button>
          ) : (
            <p className="text-xs text-muted-foreground">{lockedItemsText}</p>
          )}
        </div>
        <div className="overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>{t("pages.invoices.new.descriptionCol")}</TableHead>
                <TableHead className="w-24">{t("pages.invoices.new.qtyCol")}</TableHead>
                <TableHead className="w-32">{t("pages.invoices.new.unitPriceCol")}</TableHead>
                <TableHead className="w-32 text-right">
                  {t("pages.invoices.new.totalCol")}
                </TableHead>
                {contractItems.length === 0 && <TableHead className="w-12" />}
              </TableRow>
            </TableHeader>
            <TableBody>
              {items.map((it, idx) => {
                const lineTotal = (Number(it.quantity) || 0) * (Number(it.unit_price) || 0);
                const bal = it.estimate_item_id
                  ? contractItems.find((b) => b.id === it.estimate_item_id)
                  : null;
                const exceeds = bal ? lineTotal > bal.remaining + 0.01 : false;
                return (
                  <TableRow key={idx}>
                    <TableCell>
                      <Input
                        value={it.description}
                        onChange={(e) => updateItem(idx, { description: e.target.value })}
                      />
                      {bal && (
                        <p
                          className={`mt-1 text-xs ${exceeds ? "text-destructive" : "text-muted-foreground"}`}
                        >
                          Saldo do item: {fm(bal.remaining)}
                          {exceeds && " — excedido"}
                        </p>
                      )}
                    </TableCell>
                    <TableCell>
                      <Input
                        type="number"
                        step="0.01"
                        min="0"
                        value={it.quantity}
                        onChange={(e) => updateItem(idx, { quantity: Number(e.target.value) })}
                      />
                    </TableCell>
                    <TableCell>
                      <Input
                        type="number"
                        step="0.01"
                        min="0"
                        value={it.unit_price}
                        onChange={(e) => updateItem(idx, { unit_price: Number(e.target.value) })}
                      />
                    </TableCell>
                    <TableCell
                      className={`text-right text-sm font-medium ${exceeds ? "text-destructive" : ""}`}
                    >
                      {fm(lineTotal)}
                    </TableCell>
                    {contractItems.length === 0 && (
                      <TableCell>
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => removeItem(idx)}
                          disabled={items.length === 1}
                        >
                          <Trash2 className="h-4 w-4 text-destructive" />
                        </Button>
                      </TableCell>
                    )}
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </div>
        <div className="flex flex-col items-end gap-1 border-t border-border p-4 text-sm">
          <div className="flex w-56 justify-between">
            <span className="text-muted-foreground">{t("pages.invoices.new.subtotalLabel")}</span>
            <span>{fm(subtotal)}</span>
          </div>
          <div className="flex w-56 justify-between">
            <span className="text-muted-foreground">
              {t("pages.invoices.new.taxLabel", { percent: taxPercent })}
            </span>
            <span>{fm(taxAmount)}</span>
          </div>
          {retainagePercent > 0 && (
            <div className="flex w-56 justify-between text-muted-foreground">
              <span>{t("pages.invoices.new.retainageLabel", { percent: retainagePercent })}</span>
              <span>−{fm(retainageAmount)}</span>
            </div>
          )}
          <div className="mt-1 flex w-56 justify-between border-t border-border pt-1 text-base font-semibold">
            <span>{t("pages.invoices.new.totalLabel")}</span>
            <span>{fm(total)}</span>
          </div>
        </div>
      </div>

      <div className="flex flex-col-reverse gap-2 sm:flex-row sm:justify-end">
        <Button variant="outline" onClick={() => save("draft")} disabled={saving}>
          {t("pages.invoices.new.saveDraft")}
        </Button>
        <Button onClick={() => setConfirmSend(true)} disabled={saving} className="gap-2">
          <Lock className="h-4 w-4" />
          {saving ? t("pages.invoices.new.saving") : t("pages.invoices.new.saveSent")}
        </Button>
      </div>

      <AlertDialog open={confirmSend} onOpenChange={setConfirmSend}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2">
              <Lock className="h-5 w-5 text-muted-foreground" />
              {t("pages.invoices.new.confirmTitle")}
            </AlertDialogTitle>
            <AlertDialogDescription asChild>
              <div className="space-y-2 text-sm">
                <p dangerouslySetInnerHTML={{ __html: t("pages.invoices.new.confirmLine1") }} />
                <p>{t("pages.invoices.new.confirmLine2")}</p>
                <div className="rounded-md border border-border bg-muted/50 p-3 text-xs">
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">
                      {t("pages.invoices.new.totalLocked")}
                    </span>
                    <span className="font-semibold">{fm(total)}</span>
                  </div>
                </div>
              </div>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={saving}>
              {t("pages.invoices.new.cancel")}
            </AlertDialogCancel>
            <AlertDialogAction onClick={() => save("sent")} disabled={saving}>
              {saving ? t("pages.invoices.new.saving") : t("pages.invoices.new.confirmSendLock")}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
